import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import CountUp from "react-countup";
import { ShieldStar, Compass, Sparkle, ArrowUpRight, CheckCircle } from "@phosphor-icons/react";
import { Toaster, toast } from "sonner";
import PageShell from "@/components/dashboard/PageShell";
import { api, endpoints } from "@/lib/api";
import { resolveUserId } from "@/lib/session";

const TIER_ICONS = {
    founding_100: { icon: ShieldStar, color: "#a3e635" },
    pioneer_1000: { icon: Compass, color: "#06b6d4" },
    member: { icon: Sparkle, color: "#a1a1aa" },
};

const CommunityWall = () => {
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [me, setMe] = useState({ opted_in: false });
    const [busyOpt, setBusyOpt] = useState(false);
    const [quote, setQuote] = useState("");
    const [filter, setFilter] = useState("all"); // all | founding_100 | pioneer_1000 | member
    const [query, setQuery] = useState("");
    const [visibleCount, setVisibleCount] = useState(60);
    const uid = useMemo(() => resolveUserId(), []);

    useEffect(() => {
        let ignore = false;
        Promise.all([
            api.get(endpoints.communityWall()),
            api.get(endpoints.communityWallStatus(uid)),
        ])
            .then(([w, s]) => {
                if (ignore) return;
                setMembers(w.data.members || []);
                setMe(s.data);
                setQuote(s.data.entry?.quote || "");
                setLoading(false);
            })
            .catch(() => !ignore && setLoading(false));
        return () => {
            ignore = true;
        };
    }, [uid]);

    const refresh = async () => {
        const [w, s] = await Promise.all([
            api.get(endpoints.communityWall()),
            api.get(endpoints.communityWallStatus(uid)),
        ]);
        setMembers(w.data.members || []);
        setMe(s.data);
    };

    const optIn = async () => {
        setBusyOpt(true);
        try {
            await api.post(endpoints.communityWallOptIn(), {
                user_id: uid,
                quote: quote.trim(),
            });
            toast.success("You're on the Wall", {
                description: "Members can now see your number and Earth Score.",
            });
            await refresh();
        } catch {
            toast.error("Could not opt in — try again");
        } finally {
            setBusyOpt(false);
        }
    };

    const optOut = async () => {
        setBusyOpt(true);
        try {
            await api.post(endpoints.communityWallOptOut(), { user_id: uid });
            toast.success("Removed from the Wall");
            await refresh();
        } catch {
            toast.error("Could not remove — try again");
        } finally {
            setBusyOpt(false);
        }
    };

    // Reset pagination whenever filter or search changes
    useEffect(() => {
        setVisibleCount(60);
    }, [filter, query]);

    const q = query.trim().toLowerCase();
    const tierFiltered =
        filter === "all" ? members : members.filter((m) => m.founder_tier === filter);
    const filtered = q
        ? tierFiltered.filter((m) => {
              const num = String(m.member_number).padStart(4, "0");
              return (
                  (m.display_name || "").toLowerCase().includes(q) ||
                  num.includes(q.replace(/^#/, "")) ||
                  String(m.member_number).includes(q.replace(/^#/, ""))
              );
          })
        : tierFiltered;

    const counts = {
        founding_100: members.filter((m) => m.founder_tier === "founding_100").length,
        pioneer_1000: members.filter((m) => m.founder_tier === "pioneer_1000").length,
        member: members.filter((m) => m.founder_tier === "member").length,
    };

    return (
        <PageShell testId="community-wall-page">
            <Toaster
                theme="dark"
                position="bottom-right"
                toastOptions={{
                    style: { background: "#0a0a0a", border: "1px solid #27272a", color: "#fff" },
                }}
            />

            <header className="mb-8 rounded-lg border border-[#1f1f22] bg-gradient-to-br from-[#0b0f05] via-[#080808] to-[#050b0d] p-6 sm:p-8 lg:p-10">
                <span className="eviq-chip">Community · The Founder Wall</span>
                <h1 className="mt-4 font-editorial text-3xl leading-tight text-white sm:text-4xl md:text-5xl lg:text-6xl">
                    Every member with a number.
                    <br className="hidden sm:block" />
                    <span className="italic text-zinc-400">Every number with a story.</span>
                </h1>
                <p className="mt-4 max-w-2xl text-sm text-zinc-400 lg:text-base">
                    Opt-in only. Show the world your Founder number, tier, and Earth Score.
                    Your name and personal metrics stay private unless you choose otherwise.
                </p>

                <div className="mt-6 flex flex-wrap items-center gap-3">
                    {me.opted_in ? (
                        <>
                            <span className="inline-flex items-center gap-2 rounded-full border border-[#a3e635]/40 bg-[#0a0f05] px-4 py-2 text-sm text-[#a3e635]">
                                <CheckCircle size={14} weight="fill" />
                                You're on the wall
                            </span>
                            <button
                                type="button"
                                data-testid="wall-opt-out"
                                onClick={optOut}
                                disabled={busyOpt}
                                className="rounded-full border border-[#27272a] bg-[#0a0a0a] px-4 py-2 text-sm text-zinc-300 hover:border-[#3a3a3f] disabled:opacity-60"
                            >
                                Remove me
                            </button>
                        </>
                    ) : (
                        <>
                            <input
                                data-testid="wall-quote"
                                type="text"
                                maxLength={140}
                                value={quote}
                                onChange={(e) => setQuote(e.target.value)}
                                placeholder="Optional one-liner…"
                                className="w-full max-w-md rounded-md border border-[#27272a] bg-black px-4 py-2 text-sm text-white placeholder-zinc-600 focus:border-[#a3e635] focus:outline-none"
                            />
                            <button
                                type="button"
                                data-testid="wall-opt-in"
                                onClick={optIn}
                                disabled={busyOpt}
                                className="inline-flex items-center gap-2 rounded-full bg-[#a3e635] px-5 py-2 text-sm font-medium text-black hover:-translate-y-0.5 transition-transform disabled:opacity-60"
                            >
                                Add me to the Wall
                            </button>
                        </>
                    )}
                </div>
            </header>

            {/* Search + tier filter chips */}
            <div className="mb-4">
                <input
                    type="search"
                    data-testid="wall-search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={`Search ${members.length} founders by name or number…`}
                    className="w-full rounded-md border border-[#27272a] bg-black px-4 py-2.5 text-sm text-white placeholder-zinc-600 focus:border-[#a3e635] focus:outline-none"
                />
            </div>
            <div className="mb-6 flex flex-wrap items-center gap-2">
                {[
                    { id: "all", label: `All (${members.length})` },
                    { id: "founding_100", label: `Founders (${counts.founding_100})` },
                    { id: "pioneer_1000", label: `Pioneers (${counts.pioneer_1000})` },
                    { id: "member", label: `Members (${counts.member})` },
                ].map((chip) => (
                    <button
                        key={chip.id}
                        type="button"
                        data-testid={`wall-filter-${chip.id}`}
                        onClick={() => setFilter(chip.id)}
                        className={`rounded-full border px-3 py-1.5 text-xs transition-colors duration-200 ${
                            filter === chip.id
                                ? "border-[#a3e635] bg-[#0a0f05] text-[#a3e635]"
                                : "border-[#27272a] bg-[#050505] text-zinc-400 hover:border-[#3a3a3f] hover:text-zinc-200"
                        }`}
                    >
                        {chip.label}
                    </button>
                ))}
            </div>

            {loading && (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {[...Array(6)].map((_, i) => (
                        <div key={i} className="h-40 animate-pulse rounded-lg border border-[#1f1f22] bg-[#0a0a0a]" />
                    ))}
                </div>
            )}

            {!loading && filtered.length > 0 && (
                <>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                        {filtered.slice(0, visibleCount).map((m) => {
                            const cfg = TIER_ICONS[m.founder_tier] || TIER_ICONS.member;
                            const TierIcon = cfg.icon;
                            const padded = String(m.member_number).padStart(4, "0");
                            return (
                                <Link
                                    key={m.user_id || m.member_number}
                                    to={`/impact/founder-${padded}`}
                                    data-testid={`wall-card-${padded}`}
                                    className="group rounded-lg border border-[#1f1f22] bg-[#080808] p-5 transition-all duration-200 hover:border-[#a3e635]/40"
                                >
                                    <div className="flex items-start justify-between">
                                        <div className="flex items-center gap-2" style={{ color: cfg.color }}>
                                            <TierIcon size={14} weight="fill" />
                                            <span className="font-mono-metric text-[10px] uppercase tracking-[0.22em]">
                                                {m.founder_tier_short}
                                            </span>
                                        </div>
                                        <ArrowUpRight
                                            size={14}
                                            className="text-zinc-600 transition-colors duration-200 group-hover:text-[#a3e635]"
                                        />
                                    </div>
                                    <div className="mt-3 font-mono-metric text-4xl font-bold text-white">
                                        #{padded}
                                    </div>
                                    <div className="mt-1 font-editorial text-lg text-zinc-200">
                                        {m.display_name}
                                    </div>
                                    {m.quote && (
                                        <p className="mt-2 font-editorial text-sm italic text-zinc-500">
                                            “{m.quote}”
                                        </p>
                                    )}
                                    <div className="mt-4 flex items-baseline justify-between border-t border-[#1f1f22] pt-3">
                                        <span className="font-mono-metric text-[10px] uppercase tracking-[0.22em] text-zinc-500">
                                            Earth Score
                                        </span>
                                        <span className="font-mono-metric text-lg font-bold" style={{ color: cfg.color }}>
                                            <CountUp end={m.earth_score} separator="," duration={1.2} />
                                        </span>
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                    {filtered.length > visibleCount && (
                        <div className="mt-6 flex flex-col items-center gap-2">
                            <button
                                type="button"
                                data-testid="wall-load-more"
                                onClick={() => setVisibleCount((v) => v + 60)}
                                className="inline-flex items-center gap-2 rounded-full border border-[#a3e635]/40 bg-[#0a0f05] px-5 py-2 text-sm text-[#a3e635] hover:bg-[#141a09]"
                            >
                                Load more · {filtered.length - visibleCount} remaining
                            </button>
                            <span className="font-mono-metric text-[10px] uppercase tracking-[0.22em] text-zinc-600">
                                Showing {Math.min(visibleCount, filtered.length)} of {filtered.length}
                            </span>
                        </div>
                    )}
                </>
            )}

            {!loading && filtered.length === 0 && (
                <div className="rounded-lg border border-[#1f1f22] bg-[#080808] p-10 text-center">
                    <p className="text-sm text-zinc-500">
                        {query
                            ? `No founders match “${query}”.`
                            : "No one in this tier yet."}
                    </p>
                </div>
            )}
        </PageShell>
    );
};

export default CommunityWall;
